import { useEffect, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import { isNullish } from "@theorgchartdev/storybook";
import { RuleCompositeInput } from "../../../Composites/RuleInput/RuleCompositeInput";
import { appstr } from "@coreapp/utils/appstr";
import { QRule } from "@coreapp/CoreApp/QRule";
import { enumMatchSubs } from "@coreapp/constants";

const BuildModeOptions = onClick => {
  return [
    {
      label: appstr("No filter applied"),
      value: null,
      clickHandler: onClick
    },
    {
      label: appstr("BasicRule"),
      value: false,
      clickHandler: onClick
    },
    {
      label: appstr("ComplexRule"),
      value: true,
      clickHandler: onClick
    }
  ];
};

export const ChartBuilderRuleInput = props => {
  const [isEditing, setEditing] = useState(props.hasError ?? false);
  const [resetRule, setResetRule] = useState(null);

  const wsMgr = StateLib.GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  // When validation fails on Create, force the section into editing mode so
  // the inline errors are visible.
  useEffect(() => {
    if (props.hasAttemptedCreate && !isEditing) {
      handleStartEdit();
    }
  }, [props.hasAttemptedCreate]);

  const handleCancel = () => {
    props.setRule(resetRule);
    setEditing(false);
    setResetRule(null);
  };

  const handleSave = () => {
    props.onSave();
    setResetRule(null);
    setEditing(false);
    eventTracker.ReportEvent("chart_mode.chart_builder_rule.created");
  };

  const handleStartEdit = () => {
    if (props.rule) {
      const clonedRule = props.rule.Clone();
      setResetRule(clonedRule);
    } else {
      const rule = new QRule();
      rule.InitRule(enumMatchSubs.matchonly);
      rule.AddRulePair("", "", "", "equals", "");
      props.setRule(rule);
    }

    setEditing(true);
  };

  let actions = [];
  if (!isEditing) {
    actions = [
      {
        dataTestId: "chart-builder-filter--edit",
        onClick: handleStartEdit,
        iconName: "pen",
        tooltipProps: {
          message: appstr("Edit filter")
        },
        buttonProps: {
          ariaLabel: "Add or edit filters."
        }
      },
      {
        dataTestId: "chart-builder-filter--delete",
        onClick: () => {
          props.setRule(null);
          props.onDelete();
          setEditing(false);
        },
        disabled: isNullish(props.rule),
        iconName: "trash-can",
        iconProps: { color: "error" },
        tooltipProps: {
          message: appstr("Delete filter")
        }
      }
    ];
  }

  const handleModeChange = ({ value }) => {
    if (value === null) {
      props.setRule(null);
      props.onDelete?.();
      setEditing(false);
      setResetRule(null);
      return;
    }

    if (isNullish(props.rule)) {
      const rule = new QRule();
      rule.InitRule(enumMatchSubs.matchonly);
      rule.AddRulePair("", "", "", "equals", "");
      rule.SetIsComplex(value);
      props.setRule(rule);
      setEditing(true);
      return;
    }

    const rule = props.rule.Clone();
    rule.SetIsComplex(value);
    props.setRule(rule);
  };

  const modeValue = isNullish(props.rule) ? null : props.rule.GetIsComplex();

  return (
    <RuleCompositeInput
      allowHierarchyFields={false}
      rule={props.rule}
      setRule={props.setRule}
      editing={isEditing}
      warning={
        props.hasError
          ? appstr(
              "No results found for the selected criteria. Please adjust your selection and try again"
            )
          : undefined
      }
      onStartEdit={handleStartEdit}
      modeOptions={{
        value: modeValue,
        options: BuildModeOptions(handleModeChange)
      }}
      hoverActions={actions}
      editingActions={[
        {
          modifier: "tertiary",
          label: appstr("Cancel"),
          onClick: handleCancel
        },
        {
          modifier: "secondary",
          label: appstr("Save"),
          onClick: handleSave
        }
      ]}
      ruleInputProps={{
        basicRuleInputProps: {
          rulePairProps: {
            hasAttemptedCreate: props.hasAttemptedCreate ?? false
          }
        }
      }}
      chartData={props.chartData}
    />
  );
};
